/* @flow */
import * as React from 'react';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import actions from 'redux/actions';
import { Field, reduxForm, formValueSelector } from 'redux-form';
import { Prompt } from 'react-router-dom';
import Autosuggest from 'components/FormAutosuggest';

import validation from './validation';

import Button from 'components/Button';
import InputText from 'components/FormInputText';
import InputSelect from 'components/FormInputSelect';
import styles from './styles.module.scss';

import type {
  SaveProductionParams,
  Fetchable,
  Area,
  // Channel,
  // Broadcasting,
  // BroadcastType,
  ProductionType,
  // Platform,
  Translate,
  ReduxFormProps,
  Producer,
} from 'types';

import { isNil, get, parseInt } from 'lodash';
import common from 'utils/common';
import liferay from 'services/liferay';

const selector = formValueSelector('production');
const mapStateToProps = state => {
  return {
    isCreate: selector(state, 'id') ? false : true,
    selectedBroadcastingId: common.toNumber(selector(state, 'broadcastingId')),
    selectedChannelId: common.toNumber(selector(state, 'channelId')),
  };
};

type Props = ReduxFormProps & {
  isCreate: boolean,
  selectedBroadcastingId?: number,
  selectedChannelId?: number,
  title?: string,
  isLoading?: boolean,
  save: (params: SaveProductionParams) => mixed,
  cancel?: () => mixed,
  // RR
  router: any,
  t: Translate,
};

type State = {
  areas: Fetchable<Area[]>,
  // channels: Fetchable<Channel[]>,
  // broadcastings: Fetchable<Broadcasting[]>,
  // broadcastTypes: Fetchable<BroadcastType[]>,
  // platforms: Fetchable<Platform[]>,
  productionTypes: Fetchable<ProductionType[]>,
  producers: Fetchable<Producer[]>,
  selectedProducer: { name: String, id: number } | null,
};

const isDisabled = ({ error, fetching, data }: Fetchable<any[]>): boolean =>
  !isNil(error) || fetching || !data || data.length === 0;

const autoselect = (props, formKey: string) => (items: any[]) => {
  if (items.length === 0) {
    props.change(formKey, '');
  } else if ((props.isCreate || !props.pristine) && items.length === 1) {
    props.change(formKey, items[0].id);
  }
  return items;
};

const fetchPlatforms = (selectedChannelId, channels) => {
  const selectedChannel = channels.find(({ id }) => id === selectedChannelId);
  const platforms = get(selectedChannel, 'platforms', []);
  return Promise.resolve(platforms);
};

const isChanged = (key, from, to) => from[key] !== to[key];

class ProductionForm extends React.Component<Props, State> {
  state: State = {
    areas: { fetching: true },
    channels: { fetching: false },
    platforms: { fetching: false },
    broadcastings: { fetching: true },
    broadcastTypes: { fetching: true },
    productionTypes: { fetching: true },
    producers: { fetching: true },
    selectedProducer: null,
  };

  componentDidMount() {
    try {
      Promise.all([
        this._fetchOptions('areas', 'areaId', liferay.areas.find()),
        // this._fetchOptions('broadcastings', 'broadcastingId', liferay.broadcastings.find()).then(
        //   autoselect(this.props, 'broadcastingId')
        // ),
        // this._fetchOptions('broadcastTypes', 'broadcastTypeId', liferay.broadcastTypes.find()).then(
        //   autoselect(this.props, 'broadcastTypeId')
        // ),
        this._fetchOptions(
          'productionTypes',
          'productionTypeId',
          liferay.productionTypes.find()
        ).then(autoselect(this.props, 'productionTypeId')),
        this._fetchOptions('producers', 'producerId', liferay.producers.find()),
      ]);
    } catch (error) {
      console.warn(error);
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { selectedBroadcastingId, selectedChannelId } = this.props;
    if (isChanged('selectedBroadcastingId', prevProps, this.props)) {
      // ho cambiato l'emittente
      if (selectedBroadcastingId) {
        // ne ho selezionata una quindi richiedo i valori delle combo dipendenti (solo canali)
        this._fetchOptions(
          'channels',
          'channelId',
          liferay.channels.find(selectedBroadcastingId)
        ).then(channels => {
          // selezione automatica se c'è un unico canale
          autoselect(this.props, 'channelId')(channels);
          this.props.change('platformId', '');
          if (selectedChannelId) {
            this._fetchOptions(
              'platforms',
              'platformId',
              fetchPlatforms(selectedChannelId, channels)
            ).then(autoselect(this.props, 'platformId'));
          }
        });
      } else {
        // ho selezionato nessuna emittente quindi rimuovo i valori delle combo dipendenti
        this.setState({ channels: { fetching: false }, platforms: { fetching: false } }, () => {
          this.props.change('channelId', '');
          this.props.change('platformId', '');
        });
      }
    }
    if (isChanged('selectedChannelId', prevProps, this.props)) {
      const {
        channels: { data: channels },
      } = this.state;
      if (selectedChannelId && channels) {
        this._fetchOptions(
          'platforms',
          'platformId',
          fetchPlatforms(selectedChannelId, channels)
        ).then(autoselect(this.props, 'platformId'));
      } else {
        this.setState({ platforms: { fetching: false } }, () => {
          this.props.change('platformId', '');
        });
      }
    }
  }

  _fetchOptions(
    stateKey: string,
    formKey: string,
    fetchItemsPromise: Promise<any[]>
  ): Promise<any[]> {
    return new Promise(async (resolve, reject) => {
      this.setState({ [stateKey]: { fetching: true } });
      try {
        const items = await fetchItemsPromise;
        this.setState({ [stateKey]: { data: items, fetching: false } }, () => resolve(items));
      } catch (error) {
        console.warn(error);
        const { name, message } = error;
        this.setState(
          {
            [stateKey]: { fetching: false, error: { name, message } },
          },
          () => reject(error)
        );
      }
    });
  }

  _renderOptions = (fetchable: Fetchable<any>): React$Element<any>[] => {
    const { t } = this.props;
    if (fetchable.error) {
      return [
        <option key={'error'} value={''}>
          {fetchable.error.message || 'Errore'}
        </option>,
      ];
    } else if (fetchable.fetching) {
      return [
        <option key={'fetching'} value={''}>
          {t('LOADING')}
        </option>,
      ];
    } else if (fetchable.data) {
      if (fetchable.data.length === 0) {
        return [
          <option key={'undefined'} value={''}>
            {t('NO_VALUE_AVAILABLE')}
          </option>,
        ];
      } else {
        return [
          <option key={'null'} value={''}>
            {t('SELECT_A_VALUE')}
          </option>,
        ].concat(
          // $FlowFixMe
          fetchable.data.map(({ id, name }) => (
            <option key={id} value={id}>
              {name}
            </option>
          ))
        );
      }
    } else {
      return [<option key={'undefined'}>{t('NO_VALUE_AVAILABLE')}</option>];
    }
  };

  _handleCancelClick = () => {
    this.props.reset('production');
    this.props.cancel && this.props.cancel();
  };

  _handleSubmit = (event: any) => {
    const { handleSubmit } = this.props;
    const { selectedProducer } = this.state;
    const submitHandler = handleSubmit(({ producer, ...restValues }) =>
      this.props.save({
        producerId: selectedProducer && selectedProducer.id,
        producerName: selectedProducer && selectedProducer.name,
        ...restValues,
      })
    );
    return submitHandler(event);
  };

  render() {
    const { valid, dirty, pristine, title, isLoading = false, t } = this.props;
    const {
      areas,
      // channels,
      // broadcastings,
      // broadcastTypes,
      // platforms,
      productionTypes,
      producers,
      selectedProducer,
    } = this.state;
    return (
      <form className={styles.form} onSubmit={this._handleSubmit}>
        <Prompt when={dirty} message={location => t('CONFIRM_EXIT_DIRTY_FORM')} />
        {title && <legend className={styles.formTitle}>{title || '...'}</legend>}
        <Field component={'input'} type="hidden" name={'id'} parse={parseInt} />
        <Field
          component={InputText}
          label={t('PRODUCTION_NAME')}
          name={'name'}
          disabled={isLoading}
        />
        {/* <Field
          component={InputSelect}
          label={t('PRODUCER_NAME')}
          name={'producerId'}
          parse={parseInt}
          disabled={isLoading || isDisabled(producers)}
        >
          {this._renderOptions(producers)}
        </Field> */}
        <Field
          component={Autosuggest}
          label={t('PRODUCER_NAME')}
          name={'producer'}
          disabled={isLoading || isDisabled(producers)}
          suggestions={(producers.data || []).map(({ id, name }) => ({ id, name }))}
          getSuggestionValue={producer => (producer != null ? producer.name : '')}
          getSuggestionFromValue={name => ({ id: 0, name })}
          onChangeSuggestion={producer => this.setState({ selectedProducer: producer })}
        />
        <Field component={InputText} label={t('EDITION')} name={'edition'} disabled={isLoading} />
        {areas.data && areas.data.length > 0 && (
          <Field
            component={InputSelect}
            label={t('AREA')}
            name={'areaId'}
            parse={parseInt}
            disabled={isLoading || isDisabled(areas)}
          >
            {this._renderOptions(areas)}
          </Field>
        )}
        {/* <Field
          component={InputSelect}
          name={'broadcastingId'}
          parse={parseInt}
          label={t('BROADCASTER')}
          disabled={isLoading || isDisabled(broadcastings)}
        >
          {this._renderOptions(broadcastings)}
        </Field>
        <Field
          component={InputSelect}
          label={t('CHANNEL')}
          name={'channelId'}
          parse={parseInt}
          disabled={isLoading || isDisabled(channels)}
        >
          {this._renderOptions(channels)}
        </Field>
        <Field
          component={InputSelect}
          label={t('PLATFORM_CHANNEL')}
          name={'platformId'}
          parse={parseInt}
          disabled={isLoading || isDisabled(platforms)}
        >
          {this._renderOptions(platforms)}
        </Field>
        <Field
          component={InputSelect}
          label={t('BROADCAST_TYPE')}
          name={'broadcastTypeId'}
          parse={parseInt}
          disabled={isLoading || isDisabled(broadcastTypes)}
        >
          {this._renderOptions(broadcastTypes)}
        </Field> */}
        <Field
          component={InputSelect}
          label={t('PRODUCTION_TYPE')}
          name={'productionTypeId'}
          parse={parseInt}
          disabled={isLoading || isDisabled(productionTypes)}
        >
          {this._renderOptions(productionTypes)}
        </Field>
        <Field
          component={InputText}
          name={'quantity'}
          label={t('USAGE_QUANTITY')}
          type={'number'}
          parse={parseInt}
          disabled={isLoading}
        />
        <div className={styles.formButtons}>
          <div>
            <Button
              text={t('CANCEL')}
              onClick={this._handleCancelClick}
              disabled={isLoading || pristine}
              loading={isLoading}
            />
          </div>
          <div>
            <Button
              text={t('SAVE')}
              onClick={this._handleSubmit}
              disabled={
                isLoading || !valid || !selectedProducer || !selectedProducer.name || pristine
              }
              loading={isLoading}
              appearance={'primary'}
            />
          </div>
        </div>
      </form>
    );
  }
}

export default withTranslation()(
  connect(
    mapStateToProps,
    actions
  )(
    reduxForm({
      form: 'production',
      validate: validation,
      enableReinitialize: true,
      //$FlowFixMe
      // destroyOnUnmount: !(keys.IS_ENV_DEVELOPMENT && module.hot),
      destroyOnUnmount: true,
    })(ProductionForm)
  )
);
